import Konva from 'konva';
import { v4 as uuidv4 } from 'uuid';
import { Layer } from 'konva/lib/Layer';

import HaspPage, { HaspTemplatePage } from '../Hasp/HaspPage';

import { objectTypelookup } from '../utils/lookupTables';
import HaspObject from '../Hasp/HaspObject';
import ObjectMenu from './ObjectMenu';
import { isChild, getObjectPage } from '../utils/hasputils';
import { placeComponentLogic } from '../logic/logicClipboard.js';
import { placeComponentHass } from '../hass/hassClipboard.js';
import { migratePageHass } from '../hass/pageHassMigration.js';
import { migratePageActions } from '../logic/pageMigration.js';
import LineDrawingMixin from './LineDrawingMixin';
import ClipboardManager from './ClipboardManager';
import { exportSelectionAsTemplate, exportSelectionThumbnail } from '../exporter/TemplateExporter';
import { getAvailableCommands } from '../actions/commandRegistry.js';
import { serializeCommandAction } from '../actions/commandSerializer.js';
import { refreshBadgeConnections } from './connectionResolver';
import { createConnectionConfig, normalizeLegacyConnectionConfig } from './connectionModel';
import { routeOrthogonalConnection } from './connectionGeometry';
import ConnectionRouteShape from './ConnectionRouteShape';
import {
    connectionContainsPoint,
    placeConnectionBehindPageObjects,
    selectionAfterConnectionDrop,
} from './badgeConnectionInteraction';
import { getEventVariables } from '../actions/commandContext.js';
import { normalizeConnectionCreationDefaults, resolvedSourceConnectionColor } from './connectionCreationDefaults.js';
import { computeOffPageMaskRects, isFullyInsidePage, OFF_PAGE_MASK_OPACITY } from './offPageRecovery.js';
import {
    createTemplatePreviewState,
    getPagePreviewVisibility,
    isTemplateContextNode as isTemplateContextNodeForPage,
    resetTemplatePreviewState,
    setTemplateContextPage,
} from './templatePagePreview.js';
import { DEFAULT_SNAP_SPACING } from './ScaleStage.js';
import { pageOverlayCaptureOptions } from './pageOverlayCapture.js';
import { captureResizeAspectRatio, constrainResizeToAspectRatio } from './resizeGeometry.js';
import {
    validateDesignerObjectId,
    validateDesignerObjectIds,
    allocateDesignerObjectId,
    collectDesignerObjects,
} from '../flow/designerObjectIds.js';
import {
    canAddObjectToParent,
    canAddObjectsToParent,
    isTemplatePage,
} from '../utils/templateObjectRules.js';

class HaspObjectLayer extends Layer {
    gridSize = DEFAULT_SNAP_SPACING;
    haspobject;
    selectedObjects = [];
    transformer;
    activePage;
    pageId         = 0;   // Regular pages: 0–49
    templatePageId = 50;  // Template pages: 50+
    object_id = 1;
    theme;
    pageWidth;
    pageHeight;
    containers = [];
    // pages = [];
    pageZero = undefined;
    showId = false;
    showObjMenu = false;
    redrawOverlay = false;
    singlePageMode = true; // New: Show only one page at a time

    // Line drawing mode state
    lineDrawingMode = false;
    lineDrawingPoints = [];
    lineDrawingContainer = null;
    linePreviewLine = null;
    lineDrawingLine = null;
    lineDrawingHandlesGroup = null;
    _lastLineClickTime = 0;
    provisionalConnection = null;
    offPageMaskGroup;
    _offPageRecoveryObject = null;
    _templateContextPage = null;
    _templateContextPreviousListening = true;
    // Runtime-only Designer context for opening the same popup template from
    // different source objects/pages. This is deliberately not an attribute
    // on HaspTemplatePage, so it cannot enter design/export serialization.
    templatePreviewState = resetTemplatePreviewState();

    constructor(config) {
        super(config);
        this.theme = config.theme;
        //Layer callback is used to update the properties
        this.layerCallback = config.callback;
        //create transformer
        this.transformer = new Konva.Transformer({
            id: uuidv4(),
            rotationSnaps: [0, 45, 90, 135, 180, 225, 270, 315],
            centeredScaling: false,
            keepRatio: false,
            rotateEnabled: false,
            flipEnabled: false,
        });

        this.add(this.transformer);

        this._rubberBandRect = new Konva.Rect({
            fill: 'rgba(0, 120, 255, 0.10)',
            stroke: '#0078ff',
            strokeWidth: 1,
            dash: [4, 3],
            visible: false,
            listening: false,
        });
        this.add(this._rubberBandRect);

        this.gridSize = DEFAULT_SNAP_SPACING;
        this.connectionDefaults = normalizeConnectionCreationDefaults(config.connection_defaults);
        this.setAttr('connection_max_speed', Number(config.connection_max_speed) || 120);
        this._connectionAnimation = new Konva.Animation(frame => {
            let hasMovingConnection = false;
            this.find(node => node.obj === 'connection').forEach(connection => {
                hasMovingConnection = connection.advanceConnectionAnimation(frame.timeDiff / 1000) || hasMovingConnection;
            });
            if (!hasMovingConnection) this._connectionAnimation.stop();
        }, this);
        this.on('destroy.connection-animation', () => this._connectionAnimation.stop());
        this.width = window.innerWidth;
        this.height = window.innerHeight;

        this.objectMenu = new ObjectMenu({ x: 0, y: 0 });
        this.add(this.objectMenu);

        this.offPageMaskGroup = new Konva.Group({
            name: 'designer-off-page-mask',
            listening: false,
        });
        this._buildOffPageMask();
        this.add(this.offPageMaskGroup);

        // this.on('draw', () => {
        //     console.trace('draw event on HaspObjectLayer');
        // });

        this.on('dragstart', (e) => {
            e.cancelBubble = true; // For IE
            e.evt.preventDefault();
            e.evt.stopPropagation();
            this.handleDragStart(e);
        });

        this.on('dragmove', (e) => {
            e.cancelBubble = true; // For IE
            e.evt.preventDefault();
            e.evt.stopPropagation();
            this.handleDragMove(e.type);
        });

        this.on('dragend', (e) => {
            e.cancelBubble = true; // For IE
            e.evt.preventDefault();
            e.evt.stopPropagation();
            this.handleDragEnd(e);
        });

        this.on('mousedown', (e) => {
            this.handleMouseDown(e);
        });

        this.on('mouseup', (e) => {
            this.handleMouseUp(e);
        });

        this.on('touchstart', (e) => {
            this.handleMouseDown(e);
        });
        this.on('touchend', (e) => {
            this.handleMouseUp(e);
        });
        this.on('touchmove', (e) => {
            // A second finger means the user is pinch-zooming/panning the
            // stage, not dragging an object — let it bubble to the stage.
            if (e.evt && e.evt.touches && e.evt.touches.length > 1) return;
            // For drag-like behavior, mimic dragmove
            e.cancelBubble = true;
            if (e.evt && e.evt.preventDefault) e.evt.preventDefault();
            if (e.evt && e.evt.stopPropagation) e.evt.stopPropagation();
            this.handleDragMove(e.type);
        });

        this.on('mousemove', (evt) => {
            if (this.lineDrawingMode) this._lineDrawMouseMove(evt);
            else if (this._rubberBandActive) this._updateRubberBand(evt);
        });

        this.on('dblclick', (e) => {
            if (this.lineDrawingMode) {
                // Remove the duplicate point added by the second mousedown of the dblclick
                if (this.lineDrawingPoints.length > 4) {
                    this.lineDrawingPoints.pop();
                    this.lineDrawingPoints.pop();
                    // Remove the corresponding handle circle
                    const handles = this.lineDrawingHandlesGroup?.getChildren();
                    if (handles?.length > 0) handles[handles.length - 1].destroy();
                }
                this._lineDrawFinish();
            }
        });

        this.clipboard = new ClipboardManager(this);
    }

    // Centralizes all redraw requests. Initially wraps batchDraw() so callers
    // are decoupled from the Konva API — allows future batching/throttling.
    requestRender() {
        this.updateOffPageMask();
        this.batchDraw();
    }

    getActivePageBounds() {
        const page = this.activePage;
        if (!page) return null;
        return {
            x: page.x(),
            y: page.y(),
            width: page.width(),
            height: page.height(),
        };
    }

    isFullyInsideActivePage(node) {
        const pageBounds = this.getActivePageBounds();
        if (!pageBounds || !(node instanceof HaspObject) || node.isType('page') || node.isType('template_page')) return true;
        const box = node.getBounds?.();
        if (!box) return true;
        return isFullyInsidePage(box, pageBounds);
    }

    isCompletelyOutsideActivePage(node) {
        const pageBounds = this.getActivePageBounds();
        if (!pageBounds || !(node instanceof HaspObject) || node.isType('page') || node.isType('template_page')) return false;
        const box = node.getBounds?.();
        if (!box) return false;
        return box.x + box.width <= pageBounds.x ||
            box.y + box.height <= pageBounds.y ||
            box.x >= pageBounds.x + pageBounds.width ||
            box.y >= pageBounds.y + pageBounds.height;
    }

    _buildOffPageMask() {
        this.offPageMaskGroup?.destroyChildren();
        this._offPageMaskRects = ['top', 'left', 'right', 'bottom'].reduce((acc, name) => {
            const rect = new Konva.Rect({
                name: `designer-off-page-mask-${name}`,
                fill: '#000000',
                opacity: OFF_PAGE_MASK_OPACITY,
                listening: false,
                visible: false,
            });
            acc[name] = rect;
            this.offPageMaskGroup.add(rect);
            return acc;
        }, {});
    }

    updateOffPageMask() {
        const bounds = this.getActivePageBounds();
        const stage = this.getStage();
        if (!bounds || !stage || !this._offPageMaskRects) return;

        const scale = stage.scaleX() || 1;
        const stagePos = stage.position();
        const view = {
            x: -stagePos.x / scale,
            y: -stagePos.y / scale,
            width: stage.width() / scale,
            height: stage.height() / scale,
        };
        const rects = computeOffPageMaskRects(view, bounds);
        this._offPageMaskRects.top.setAttrs(rects.top);
        this._offPageMaskRects.left.setAttrs(rects.left);
        this._offPageMaskRects.right.setAttrs(rects.right);
        this._offPageMaskRects.bottom.setAttrs(rects.bottom);

        this.offPageMaskGroup.moveToTop();
        this.transformer.moveToTop();
        this.objectMenu.moveToTop?.();
    }

    _resolveHaspObjectFromEventTarget(target) {
        if (target instanceof HaspObject) return target;
        let ancestor = target?.parent;
        while (ancestor && !(ancestor instanceof HaspObject)) {
            ancestor = ancestor.parent;
        }
        return ancestor instanceof HaspObject ? ancestor : null;
    }

    _connectionAtPointer(point) {
        if (!point || !this.activePage) return null;
        const connections = this.activePage.find?.(node => node.obj === 'connection') ?? [];
        return [...connections].reverse().find(connection => connectionContainsPoint(connection, point)) ?? null;
    }

    setOffPageRecoveryNode(node) {
        this._deselectCurrent();
        this.haspobject = node;
        this._offPageRecoveryObject = node;
        this.transformer.nodes([]);
        this.objectMenu.setNode(undefined);
        this.layerCallback('off-page-recovery-selected', node);
        this.updateOffPageMask();
        this.batchDraw();
    }

    clearOffPageRecoveryNode(node) {
        if (!node || this._offPageRecoveryObject === node) {
            this._offPageRecoveryObject = null;
        }
    }

    _handleOffPageDragStart(node) {
        this.setOffPageRecoveryNode(node);
        this._preDragPos = { x: node.x(), y: node.y() };
    }

    _handleOffPageDragMove(node) {
        if (node?.obj === 'badge') this.refreshBadgeConnections();
        this.updateOffPageMask();
        this.layerCallback('off-page-drag-move', node);
    }

    _handleOffPageDragEnd(node) {
        if (node?.obj === 'badge') this.refreshBadgeConnections();
        if (!this.isCompletelyOutsideActivePage(node)) this.clearOffPageRecoveryNode(node);
        this.updateOffPageMask();
        this.layerCallback('off-page-drag-end', node);
    }

    requestConnectionAnimation() {
        if (!this._connectionAnimation.isRunning()) this._connectionAnimation.start();
    }

    setConnectionMaxSpeed(value) {
        const speed = Number(value);
        this.setAttr('connection_max_speed', Number.isFinite(speed) && speed > 0 ? speed : 120);
        this.find(node => node.obj === 'connection').forEach(connection => connection.updateFlowState());
    }

    // Sets the active page and notifies GuidelineManager so per-page guidelines
    // are shown/hidden and the ruler handles are refreshed.
    _setActivePage(page) {
        this.activePage = page;
        const stage = this.getStage();
        if (stage?.guidelineManager) {
            stage.guidelineManager.setActivePageId(page?.haspid ?? null);
            stage.rulerCanvas?.update();
        }
    }

    _deselectCurrent() {
        this.selectedObjects.forEach(o => {
            if (o instanceof HaspObject && typeof o.setSelected === 'function') o.setSelected(false);
        });
        this.selectedObjects = [];
        if (this.haspobject instanceof HaspObject && typeof this.haspobject.setSelected === 'function') {
            this.haspobject.setSelected(false);
        }
        const stage = this.getStage();
        if (stage && stage.snapLayer) {
            stage.snapLayer.clearGuides();
            stage.snapLayer.flushGuides();
        }
    }

    handleDragStart(e) {
        const dragObject = this._resolveHaspObjectFromEventTarget(e?.target);

        // A parent obj can own the Konva hit target while the pointer is
        // actually on a page-level connection routed between nested badges.
        // The connection is the selected design object in that case, so do
        // not let Konva start dragging the covering parent container.
        if (this.haspobject?.isType?.('connection') && dragObject !== this.haspobject) {
            e?.target?.stopDrag?.();
            return;
        }

        if (dragObject instanceof HaspObject && this.isCompletelyOutsideActivePage(dragObject)) {
            this._handleOffPageDragStart(dragObject);
            return;
        }

        if (!(this.haspobject instanceof HaspObject)) return;

        this._preDragPos = { x: this.haspobject.x(), y: this.haspobject.y() };

        // For multi-select: capture pre-drag positions of all selected objects once
        if (this.selectedObjects.length > 1) {
            if (!this._multiDragCount) {
                this._multiDragPositions = new Map();
                this.selectedObjects.forEach(obj => {
                    this._multiDragPositions.set(obj.id(), { x: obj.x(), y: obj.y() });
                });
            }
            this._multiDragCount = (this._multiDragCount ?? 0) + 1;
            return; // skip snap setup for secondary objects
        }
        this._multiDragPositions = null;
        this._multiDragCount = 0;

        this.haspobject.transformStart();

        // Wire snap via dragBoundFunc so Konva can't override the snapped position
        if (!this.haspobject.isType('page') && !this.haspobject.isType('mtrx_btn') && !this.haspobject.isType('msgbox_btn')) {
            const stage = this.getStage();
            if (stage && stage.snapManager && stage.snapLayer) {
                // Collect sibling snap targets once — not every frame
                const snapTargets = stage.snapManager.collectSnapTargets(this.haspobject, stage.guidelineManager);
                // Node size in stage-content units (doesn't change during drag)
                const b = this.haspobject.getBounds();
                const nodeSize = { width: b.width, height: b.height };

                const snapManager      = stage.snapManager;
                const snapLayer        = stage.snapLayer;
                const guidelineManager = stage.guidelineManager;
                const obj              = this.haspobject;

                this.haspobject.dragBoundFunc((absPos) => {
                    // absPos is in Konva's "absolute" space (screen px relative to canvas,
                    // consistent with getAbsolutePosition). Convert to stage-content coords.
                    const scaleX   = stage.scaleX();
                    const scaleY   = stage.scaleY();
                    const stagePos = stage.position();
                    const contentPos = {
                        x: (absPos.x - stagePos.x) / scaleX,
                        y: (absPos.y - stagePos.y) / scaleY,
                    };

                    const r = snapManager.findSnapForContentPos(contentPos, nodeSize, snapTargets);

                    // Draw guides in stage-content space (SnapLayer renders there)
                    snapLayer.clearGuides();
                    if (r.guideX !== undefined)
                        snapLayer.drawVerticalGuide(r.guideX, r.guideXyMin, r.guideXyMax);
                    if (r.guideY !== undefined)
                        snapLayer.drawHorizontalGuide(r.guideY, r.guideYxMin, r.guideYxMax);
                    snapLayer.flushGuides();

                    // Update guideline attachment state based on current snap result.
                    // Snapping to a guideline creates an attachment; moving away breaks it.
                    if (guidelineManager) {
                        const newVertId  = r.snappedVerticalGuidelineId ?? null;
                        const newHorizId = r.snappedHorizontalGuidelineId ?? null;

                        if (newVertId !== null) {
                            guidelineManager.attachObject(newVertId, obj);
                        } else if (obj.attachedGuidelines.vertical !== null) {
                            guidelineManager.detachObject(obj, 'vertical');
                        }

                        if (newHorizId !== null) {
                            guidelineManager.attachObject(newHorizId, obj);
                        } else if (obj.attachedGuidelines.horizontal !== null) {
                            guidelineManager.detachObject(obj, 'horizontal');
                        }
                    }

                    // Return snapped position back in Konva absolute space
                    return {
                        x: r.snapX * scaleX + stagePos.x,
                        y: r.snapY * scaleY + stagePos.y,
                    };
                });
            }
        }
    }

    handleDragMove(event) {
        if (this._offPageRecoveryObject instanceof HaspObject) {
            this._handleOffPageDragMove(this._offPageRecoveryObject);
            return;
        }

        if (!this.haspobject) return;

        // Trigger live redraw for objects that need it (e.g. custom path objects)
        if (!this.haspobject.isType('page') && !this.haspobject.isType('img')) {
            const transformObject = this.haspobject;
            if (typeof transformObject.redraw === 'function' && transformObject.needsLiveDragRedraw) {
                transformObject.redraw(event);
            }
        }

        if (this.objectMenu) this.objectMenu.follow();
        if (this.haspobject?.obj === 'badge') this.refreshBadgeConnections();

        // Throttle layer callback to ~60fps
        const now = Date.now();
        if (!this._lastCallbackTime || now - this._lastCallbackTime > 16) {
            this.layerCallback('drag-move', this.haspobject);
            this._lastCallbackTime = now;
        }
        this.updateOffPageMask();
    }

    handleDragEnd(e) {
        // The Konva Transformer is internally draggable and fires its own dragend — ignore it
        if (e.target === this.transformer) return;

        const dragObject = this._resolveHaspObjectFromEventTarget(e?.target) ?? this._offPageRecoveryObject;
        if (dragObject instanceof HaspObject && this._offPageRecoveryObject === dragObject) {
            this._handleOffPageDragEnd(dragObject);
            return;
        }

        this._lastCallbackTime = undefined;

        // Multi-select drag: decrement counter; fire batch callback on last dragend
        if (this._multiDragPositions) {
            this._multiDragCount = Math.max(0, (this._multiDragCount ?? 1) - 1);
            if (this._multiDragCount === 0) {
                if (this.selectedObjects.some(o => getObjectPage(o)?.haspid === 0)) {
                    this.updatePageZeroOverlay('handleDragEnd');
                }
                this.objectMenu.update();
                this.layerCallback('multi-drag-end', this.selectedObjects);
                this._multiDragPositions = null;
            }
            return;
        }

        // Remove snap constraint and clear guides
        if (this.haspobject instanceof HaspObject) {
            this.haspobject.dragBoundFunc(undefined);
        }
        const stage = this.getStage();
        if (stage && stage.snapLayer) stage.snapLayer.clearGuides();

        if (getObjectPage(this.haspobject)?.haspid === 0) {
            this.updatePageZeroOverlay('handleDragEnd');
        }

        // Ensure overlay stays on top after drag
        if (this.haspobject instanceof HaspObject) {
            const objectPage = getObjectPage(this.haspobject);
            if (this.redrawOverlay && objectPage && objectPage.pageZeroOverlay) {
                objectPage.pageZeroOverlay.moveToTop();
            }
        }

        if (this.haspobject instanceof HaspObject) {
            this.layerCallback('drag-end', this.haspobject);
            this.haspobject.transformEnd();
        }

        this.objectMenu.update();
        this.refreshBadgeConnections();
        this.updateOffPageMask();
    }

    /**
     * save selected nodes ans clear all selected nodes for performance
     */
    stageDragStart() {
        this.selectedNodes = this.transformer.nodes();
        this.transformer.nodes([]);
        if (this.haspobject instanceof HaspObject) {
            this.haspobject.transformStart();
        }
    }

    /**
     * resore selected nodes
     */
    stageDragEnd() {
        this.transformer.nodes(this.selectedNodes);
        if (this.haspobject instanceof HaspObject) {
            // this.haspobject.transformEnd();
            // Panning the stage moves the viewport, not any object — page 0's
            // content is unchanged, so no overlay recapture is needed here.

            // Ensure overlay stays on top after stage drag
            const objectPage = getObjectPage(this.haspobject);
            if (this.redrawOverlay && objectPage && objectPage.pageZeroOverlay) {
                objectPage.pageZeroOverlay.moveToTop();
            }
        }
    }

    /**
     * unselect all nodes for performance
     */
    stageWheelStart() {
        this.transformer.nodes([]);
    }

    showObjectMenu(node) {
        if (node && node instanceof HaspObject && this.showObjMenu && this.objectMenu && node.has_objectmenu) {
            this.objectMenu.setNode(node);
            this.objectMenu.follow();
        } else {
            this.objectMenu.setNode(undefined);
        }
    }

    moveIntoView() {
        const stage = this.getStage();
        if (!stage || !this.haspobject) return;

        const scale = stage.scaleX();

        // getAbsolutePosition() returns the node's top-left corner in canvas screen pixels
        const absPos = this.haspobject.getAbsolutePosition();
        const w = (this.haspobject.width?.()  ?? 0) * scale;
        const h = (this.haspobject.height?.() ?? 0) * scale;

        // Check whether any part of the object overlaps the visible viewport
        const inView = absPos.x + w > 0 &&
                       absPos.x     < stage.width() &&
                       absPos.y + h > 0 &&
                       absPos.y     < stage.height();

        // Already visible — leave the canvas exactly where it is
        if (inView) return;

        // Object is off-screen: pan to center it
        const pos = stage.getAbsolutePos(this.haspobject); // content-space coords
        stage.position({
            x: stage.width()  / 2 - pos.x * scale,
            y: stage.height() / 2 - pos.y * scale,
        });
        stage.batchDraw();
    }

    isTemplateContextNode(node) {
        return isTemplateContextNodeForPage(this._templateContextPage, node);
    }

    setSelectedNode(selectedNode) {
        if (this.isTemplateContextNode(selectedNode)) return;

        if (selectedNode instanceof HaspObject && this.isCompletelyOutsideActivePage(selectedNode)) {
            this.setOffPageRecoveryNode(selectedNode);
            return;
        }

        // Clear entire multi-selection first
        this.selectedObjects.forEach(o => {
            if (o instanceof HaspObject && typeof o.setSelected === 'function') o.setSelected(false);
        });
        this.selectedObjects = [];
        // Deselect the previous primary selection (e.g. hide line handles)
        if (this.haspobject instanceof HaspObject && typeof this.haspobject.setSelected === 'function') {
            this.haspobject.setSelected(false);
        }
        const stage = this.getStage();
        if (stage && stage.snapLayer) {
            stage.snapLayer.clearGuides();
            stage.snapLayer.flushGuides();
        }
        this.haspobject = selectedNode;
        this._offPageRecoveryObject = null;
        if (selectedNode instanceof HaspObject) this.selectedObjects = [selectedNode];
        // this.moveIntoView();
        if (this.haspobject instanceof HaspObject) {

            if (this.haspobject.isType('page') || this.haspobject.isType('template_page')) {
                // Selecting a template from the Designer UI starts a normal
                // template context without a source. Action-driven previews
                // use previewTemplate() and retain their source object.
                if (this.haspobject.isType('template_page')) {
                    this.setTemplatePreviewContext(this.haspobject);
                } else {
                    this.resetTemplatePreviewContext();
                }
                if (this.activePage !== undefined) {
                    this.activePage.setActive(false);
                }
                this._setActivePage(this.haspobject);
                this.transformer.nodes([]);
                this.activePage.setActive(true);
                
                // Refresh visibility and restore/apply any read-only template
                // context in both single-page and overview modes.
                this.updatePageVisibility();
            } else {
                this.transformer.moveToTop();
                this.updateTransformer();
            }
        }
        // Activate selection for objects that support it (e.g. HaspLine handles)
        if (this.haspobject instanceof HaspObject && typeof this.haspobject.setSelected === 'function') {
            this.haspobject.setSelected(true);
        }
        this.layerCallback('node-selected', this.haspobject)
        this.showObjectMenu(selectedNode);
        this.updateOffPageMask();
    }


    updateObjectMenu(node) {
        this.objectMenu.update(node);
    }

    /**
     * Move the object from one parent to another.
     * When we move between pages we should check the haspid
     * these should be unique
     * @param {*} sourceId 
     * @param {*} targetId 
     */
    moveNode(sourceId, targetId) {
        const srcObject = this.findObjectById(this, Number(sourceId));
        const targetObject = this.findObjectById(this, Number(targetId));
        if (targetObject && srcObject) {
            //check if the source is a parent of the target
            if (isChild(targetObject, srcObject)) {
                alert('Do not drop a node on one of its childnodes!')
                return false;
            }
            if (isTemplatePage(getObjectPage(srcObject)) || isTemplatePage(targetObject)) {
                const result = canAddObjectToParent(targetObject, srcObject.obj, { movingObject: srcObject });
                if (!result.valid) {
                    this.layerCallback('object-move-error', { message: result.reason, object: srcObject });
                    return false;
                }
            }
            targetObject.addItem(srcObject);
            this.layerCallback('node-moved', srcObject);
            return true;
        }
        return false;
    }

    findObjectById(node, id) {
        if (node._id === id) {
            return node;
        }

        if (node.hasChildren && node.hasChildren()) {
            const children = node.getChildren();
            for (let i = 0; i < children.length; i++) {
                const result = this.findObjectById(children[i], id);
                if (result) {
                    return result;
                }
            }
        }

        return null;
    }

    handleMouseDown(e) {
        // Drawing mode intercepts all clicks
        if (this.lineDrawingMode) {
            this._lineDrawClick(e);
            return;
        }

        const isMultiSelect = !!(e.evt && (e.evt.ctrlKey || e.evt.metaKey || e.evt.shiftKey));
        const prevHaspObject = this.haspobject;

        // clicked on empty stage — deselect (or keep selection) and start rubber-band
        if (e.target === e.target.getStage()) {
            if (!isMultiSelect) {
                this._deselectCurrent();
                this.haspobject = undefined;
                this.updateTransformer();
            }
            this._startRubberBand(e);
            return;
        }
        // clicked on transformer - move it to top layer
        if (e.target.getParent().className === 'Transformer') {
            this.transformer.moveToTop();
            return;
        }

        const selectedNode = e.target;
        if (selectedNode !== undefined && selectedNode !== null) {
            //return if the selected node is an object menu
            if (selectedNode.attrs.obj === 'object_menu') {
                return;
            }

            //find the page of the selected node
            const objectPage = getObjectPage(selectedNode);
            if (objectPage !== undefined && objectPage !== this.activePage) {
                if (!isMultiSelect) {
                    this.activePage.setActive(false);
                    objectPage.setActive(true);
                    this._setActivePage(objectPage);
                    this.haspobject = selectedNode.parent;
                    this.transformer.nodes([]);
                }
            }

            const targetObject = this._resolveHaspObjectFromEventTarget(selectedNode);
            const connectionAtPointer = targetObject?.isType?.('badge') || targetObject?.isType?.('connection')
                ? null
                : this._connectionAtPointer(this.activePage?.getRelativePointerPosition?.());

            if (connectionAtPointer) {
                // A connection between badges nested in an obj is covered by
                // that obj's hit region. Prefer the page-level connection
                // when the pointer is on its route, while badge clicks still
                // win at the endpoints.
                this.haspobject = connectionAtPointer;
            } else if (this.haspobject instanceof HaspObject && this.haspobject.isType('mtrx_btn')) {
                this.haspobject.setSelected(false);
            }
            if (!connectionAtPointer && this.haspobject instanceof HaspObject && this.haspobject.isType('msgbox_btn')) {
                this.haspobject.setSelected(false);
            }

            if (connectionAtPointer) {
                // Already resolved above; keep the target-specific branches
                // below from replacing it with the parent obj.
            } else if (selectedNode.attrs.obj === "tab_button") {
                const tab = selectedNode.attrs.tab;
                this.haspobject = tab.tabview;
                this.haspobject.setSelectedTab(tab);
                this.haspobject.drawTabs();
            } else if (selectedNode.attrs.obj === "mtrx_btn") {
                this.haspobject = selectedNode.parent;
                this.haspobject.setSelected(true);
            } else if (selectedNode.attrs.obj === "msgbox_btn") {
                this.haspobject = selectedNode.parent;
                this.haspobject.setSelected(true);
            } else if (selectedNode.attrs.obj === 'connection_path_hit') {
                // The visible connection route is deliberately non-listening;
                // the wider hit route is the canvas selection target.
                this.haspobject = selectedNode.parent;
            } else if (selectedNode instanceof Konva.Rect || selectedNode instanceof Konva.Image) {
                this.haspobject = selectedNode.parent;
            } else if (selectedNode instanceof Konva.Line && selectedNode.name() === 'line-shape') {
                this.haspobject = selectedNode.parent;
            } else {
                // For any other node (e.g. a sub-Group inside a complex widget),
                // walk up the parent chain to find the nearest HaspObject ancestor.
                let ancestor = selectedNode.parent;
                while (ancestor && !(ancestor instanceof HaspObject)) {
                    ancestor = ancestor.parent;
                }
                if (ancestor instanceof HaspObject) {
                    this.haspobject = ancestor;
                } else {
                    this.transformer.moveToTop();
                }
            }
            this.showObjectMenu(this.haspobject);
        } else if (this.haspobject instanceof HaspObject) {
            this.haspobject.transformStart();
        } else {
            this.haspobject = undefined;
        }

        // Apply selection: toggle into multi-select or replace with single select
        const resolved = this.haspobject;
        if (resolved instanceof HaspObject && this.isCompletelyOutsideActivePage(resolved)) {
            this.setOffPageRecoveryNode(resolved);
            return;
        }

        if (isMultiSelect && resolved instanceof HaspObject &&
            !resolved.isType('page') && !resolved.isType('template_page')) {
            const idx = this.selectedObjects.indexOf(resolved);
            if (idx >= 0) {
                // Deselect from group
                resolved.setSelected(false);
                this.selectedObjects.splice(idx, 1);
                this.haspobject = this.selectedObjects[this.selectedObjects.length - 1];
            } else {
                // Add previous single-selection to group on first ctrl+click
                if (this.selectedObjects.length === 0 && prevHaspObject instanceof HaspObject) {
                    this.selectedObjects.push(prevHaspObject);
                    prevHaspObject.setSelected(true);
                }
                resolved.setSelected(true);
                this.selectedObjects.push(resolved);
            }
        } else {
            // Single select: clear previous, set new
            if (prevHaspObject !== resolved) {
                this.selectedObjects.forEach(o => {
                    if (o instanceof HaspObject && typeof o.setSelected === 'function') o.setSelected(false);
                });
                if (prevHaspObject instanceof HaspObject && typeof prevHaspObject.setSelected === 'function') {
                    prevHaspObject.setSelected(false);
                }
            }
            this.selectedObjects = resolved instanceof HaspObject ? [resolved] : [];
            if (resolved instanceof HaspObject && typeof resolved.setSelected === 'function') {
                resolved.setSelected(true);
            }
        }

        this.updateTransformer();
        this.layerCallback('mouse-down', this.haspobject);
    }

    handleMouseUp(e) {
        if (this._rubberBandActive) {
            this._finishRubberBand(e);
            return;
        }
        if (this._offPageRecoveryObject instanceof HaspObject && this.isCompletelyOutsideActivePage(this._offPageRecoveryObject)) {
            return;
        }
        if (this.haspobject !== undefined) {
            this.layerCallback('mouse-up', this.haspobject);
            if (this.haspobject instanceof HaspObject)
                this.haspobject.transformEnd();
        }
    }

    transformStart(haspObject) {
        this.objectMenu.update();

        if (haspObject && !haspObject.isType?.('page')) {
            this._preTransformState = {
                x: haspObject.x(), y: haspObject.y(),
                w: haspObject.width(), h: haspObject.height(),
            };
            const resizeMode = haspObject.getResizeMode?.();
            this._transformObject = haspObject;
            this._transformAspectRatio = captureResizeAspectRatio({
                width: haspObject.width(),
                height: haspObject.height(),
                resizeMode,
            });
            this._transformAnchor = this.transformer.getActiveAnchor?.() || '';
        }

        if (!haspObject || !haspObject.isType || haspObject.isType('page')) return;
        const stage = this.getStage();
        if (!(stage && stage.snapManager && stage.snapLayer)) return;

        const snapTargets = stage.snapManager.collectSnapTargets(haspObject, stage.guidelineManager);
        const snapManager = stage.snapManager;
        const snapLayer   = stage.snapLayer;
        const tr          = this.transformer;

        this.transformer.boundBoxFunc((_oldBox, newBox) => {
            const anchor = tr.getActiveAnchor() || '';
            const leftMoves  = anchor.includes('left');
            const rightMoves = anchor.includes('right');
            const topMoves   = anchor.includes('top');
            const botMoves   = anchor.includes('bottom');

            const scaleX = stage.scaleX();
            const scaleY = stage.scaleY();
            const sp     = stage.position();

            // Convert a screen-pixel x/y to stage-content
            const cx = sx => (sx - sp.x) / scaleX;
            const cy = sy => (sy - sp.y) / scaleY;
            // Convert a stage-content x/y back to screen-pixel
            const sx = cx2 => cx2 * scaleX + sp.x;
            const sy = cy2 => cy2 * scaleY + sp.y;

            // Current box edges in stage-content (for guide extents)
            const bLeft   = cx(newBox.x);
            const bTop    = cy(newBox.y);
            const bRight  = cx(newBox.x + newBox.width);
            const bBottom = cy(newBox.y + newBox.height);

            const result = { ...newBox };
            let guideX, guideXyMin, guideXyMax;
            let guideY, guideYxMin, guideYxMax;
            let snapX, snapY;

            // ── X axis ──────────────────────────────────────────────────────
            const xEdge = leftMoves ? bLeft : rightMoves ? bRight : undefined;
            if (xEdge !== undefined) {
                snapX = snapManager.findSnapForEdge(xEdge, snapTargets.vertical);
                if (snapX) {
                    const snappedSX = sx(snapX.snappedPos);
                    if (leftMoves) {
                        result.width += result.x - snappedSX;
                        result.x = snappedSX;
                    } else {
                        result.width = snappedSX - result.x;
                    }
                    guideX    = snapX.snappedPos;
                    guideXyMin = Math.min(bTop,    snapX.bounds.y);
                    guideXyMax = Math.max(bBottom, snapX.bounds.y + snapX.bounds.height);
                }
            }

            // ── Y axis ──────────────────────────────────────────────────────
            const yEdge = topMoves ? bTop : botMoves ? bBottom : undefined;
            if (yEdge !== undefined) {
                snapY = snapManager.findSnapForEdge(yEdge, snapTargets.horizontal);
                if (snapY) {
                    const snappedSY = sy(snapY.snappedPos);
                    if (topMoves) {
                        result.height += result.y - snappedSY;
                        result.y = snappedSY;
                    } else {
                        result.height = snappedSY - result.y;
                    }
                    guideY    = snapY.snappedPos;
                    guideYxMin = Math.min(bLeft,  snapY.bounds.x);
                    guideYxMax = Math.max(bRight, snapY.bounds.x + snapY.bounds.width);
                }
            }

            // Snapping proposes a new box, but ratio mode is a hard constraint
            // across the whole pipeline. Use the snapped axis as the primary
            // dimension so that the active snap edge remains the controlling
            // edge; derive the other dimension from the captured ratio.
            let primaryAxis;
            if (guideX !== undefined && guideY === undefined) {
                primaryAxis = 'width';
            } else if (guideY !== undefined && guideX === undefined) {
                primaryAxis = 'height';
            } else if (guideX !== undefined && guideY !== undefined) {
                const widthDelta = Math.abs(result.width - newBox.width) / scaleX;
                const heightDelta = Math.abs(result.height - newBox.height) / scaleY;
                primaryAxis = widthDelta >= heightDelta ? 'width' : 'height';
            }

            const ratioGeometry = this.constrainTransformGeometry(haspObject, {
                x: cx(result.x),
                y: cy(result.y),
                width: result.width / scaleX,
                height: result.height / scaleY,
            }, { primaryAxis });
            if (ratioGeometry) {
                result.x = sx(ratioGeometry.x);
                result.y = sy(ratioGeometry.y);
                result.width = ratioGeometry.width * scaleX;
                result.height = ratioGeometry.height * scaleY;
            }

            // The ratio correction can move the non-primary edge. Guides must
            // describe the final object edge, never the pre-correction snap
            // proposal, otherwise the visual snapline lies about the result.
            if (guideX !== undefined) {
                guideX = leftMoves ? cx(result.x) : cx(result.x + result.width);
                guideXyMin = Math.min(cy(result.y), snapX.bounds.y);
                guideXyMax = Math.max(cy(result.y + result.height), snapX.bounds.y + snapX.bounds.height);
            }
            if (guideY !== undefined) {
                guideY = topMoves ? cy(result.y) : cy(result.y + result.height);
                guideYxMin = Math.min(cx(result.x), snapY.bounds.x);
                guideYxMax = Math.max(cx(result.x + result.width), snapY.bounds.x + snapY.bounds.width);
            }

            snapLayer.clearGuides();
            if (guideX !== undefined)
                snapLayer.drawVerticalGuide(guideX, guideXyMin, guideXyMax);
            if (guideY !== undefined)
                snapLayer.drawHorizontalGuide(guideY, guideYxMin, guideYxMax);
            snapLayer.flushGuides();

            return result;
        });
    }

    transform() {
        this.objectMenu.update();
    }

    /**
     * Return the integer resize geometry for a ratio-constrained object.
     * ObjectGroup owns the common Konva scale-to-size conversion, while this
     * layer owns the interaction-wide ratio captured in transformStart().
     */
    constrainTransformGeometry(haspObject, geometry, { primaryAxis } = {}) {
        if (!this._isTransformObject(haspObject) || !this._transformAspectRatio) return null;

        const activeAnchor = this.transformer.getActiveAnchor?.() || this._transformAnchor || '';
        if (activeAnchor) this._transformAnchor = activeAnchor;

        return constrainResizeToAspectRatio({
            ...geometry,
            aspectRatio: this._transformAspectRatio,
            minWidth: haspObject.min_width,
            minHeight: haspObject.min_height,
            anchor: activeAnchor,
            primaryAxis,
        });
    }

    _isTransformObject(haspObject) {
        const activeObject = this._transformObject;
        return haspObject === activeObject ||
            (haspObject?.id && activeObject?.id && haspObject.id() === activeObject.id());
    }

    _finalizeRatioTransform(haspObject) {
        if (!this._isTransformObject(haspObject) || !this._transformAspectRatio) return;

        const geometry = this.constrainTransformGeometry(haspObject, {
            x: haspObject.x(),
            y: haspObject.y(),
            width: haspObject.width(),
            height: haspObject.height(),
        });
        if (!geometry) return;

        if (geometry.x !== haspObject.x() || geometry.y !== haspObject.y() ||
            geometry.width !== haspObject.width() || geometry.height !== haspObject.height()) {
            haspObject.x(geometry.x);
            haspObject.y(geometry.y);
            haspObject.setNewSize?.({ width: geometry.width, height: geometry.height });
        }
    }

    transformEnd(haspObject) {
        this._finalizeRatioTransform(haspObject);
        this.layerCallback('transform-end', haspObject);
        this.transformer.forceUpdate();
        this.transformer.boundBoxFunc(undefined);

        const stage = this.getStage();
        if (stage && stage.snapLayer) stage.snapLayer.clearGuides();

        const objectPage = getObjectPage(haspObject);
        if (objectPage?.haspid === 0) {
            this.updatePageZeroOverlay('transformEnd');
        }

        if (this.redrawOverlay && objectPage && objectPage.pageZeroOverlay) {
            objectPage.pageZeroOverlay.moveToTop();
        }

        this.objectMenu.update();
        this._transformObject = null;
        this._transformAspectRatio = null;
        this._transformAnchor = '';
    }

    resetTransformer() {
        this.haspobject = undefined;
        this.updateTransformer();
    }

    updateTransformer() {
        if (this.haspobject instanceof HaspObject && this.isCompletelyOutsideActivePage(this.haspobject)) {
            this.transformer.nodes([]);
            this.transformer.enabledAnchors([]);
            return;
        }

        const transformable = this.selectedObjects.filter(o => o instanceof HaspObject && o.can_transform);

        if (transformable.length > 1) {
            // Multi-select: drag only, no resize or rotate
            this.transformer.nodes(transformable);
            this.transformer.moveToTop();
            this.transformer.enabledAnchors([]);
            this.transformer.keepRatio(false);
            this.transformer.rotateEnabled(false);
            return;
        }

        if (this.haspobject !== undefined && this.haspobject instanceof HaspObject && this.haspobject.can_transform) {
            this.transformer.nodes([this.haspobject]);
            this.transformer.moveToTop();
            // getResizeMode() is the single source of truth for which anchors
            // apply — widgets with state-dependent resize behavior (e.g.
            // HaspDropdown while collapsed) override it instead of being
            // special-cased here.
            switch (this.haspobject.getResizeMode()) {
                case 'auto':
                case 'none':
                    this.transformer.keepRatio(false);
                    this.transformer.enabledAnchors([]);
                    break;
                case 'keep_ratio':
                    // The Designer's boundBoxFunc is the single ratio
                    // authority. Konva must not apply a second, independent
                    // keep-ratio correction before snapping is processed.
                    this.transformer.keepRatio(false);
                    this.transformer.enabledAnchors(['top-left', 'top-right', 'bottom-left', 'bottom-right']);
                    break;
                case 'horizontal':
                    this.transformer.keepRatio(false);
                    this.transformer.enabledAnchors(['middle-left', 'middle-right']);
                    break;
                case 'vertical':
                    this.transformer.keepRatio(false);
                    this.transformer.enabledAnchors(['bottom-center', 'top-center']);
                    break;
                default: // 'both'
                    this.transformer.keepRatio(false);
                    this.transformer.enabledAnchors(['top-left', 'top-center', 'top-right', 'middle-right', 'middle-left', 'bottom-left', 'bottom-center', 'bottom-right']);
            }
            if (this.haspobject.has_rotation) {
                this.transformer.rotateEnabled(true);
            } else {
                this.transformer.rotateEnabled(false);
            }
            this.transformer.nodes([this.haspobject]);
        } else {
            this.transformer.nodes([]);
            this.transformer.enabledAnchors([]);
        }
    }

    deleteSelectedNodes() {
        const toDelete = this.selectedObjects.length > 0
            ? [...this.selectedObjects]
            : (this.haspobject ? [this.haspobject] : []);

        // Capture before destroy() detaches nodes from the tree, so
        // getObjectPage() can still find their page afterwards.
        const affectsPageZero = toDelete.some(obj => getObjectPage(obj)?.haspid === 0);

        toDelete.forEach(obj => {
            if (!(obj instanceof HaspObject)) return;
            if (obj.isType('tab')) {
                obj.tabview.deleteActiveTab();
            } else {
                obj.destroy();
            }
        });

        this.selectedObjects = [];
        this.haspobject = undefined;
        this.transformer.nodes([]);
        this.refreshBadgeConnections();
        if (affectsPageZero) this.updatePageZeroOverlay('deleteSelectedNodes');
    }

    showObjectId(show) {
        this.showId = show;
        const event = new Event("showid");
        event.showId = show;
        this.getChildren().forEach(element => {
            if (element instanceof HaspObject) {
                element.dispatchEvent(event);
            }
        });

    }

    showObjectDetails(show) {
        this.showObjMenu = show;
        this.showObjectMenu(this.haspobject);
    }

    clearAll() {
        const children = [...this.getChildren()];
        //we first need to get all pages before destroying
        children.forEach(element => {
            if (element instanceof HaspPage) {
                element.destroy();
            }
        });
        //reset page ID counters
        this.pageId         = 0;
        this.templatePageId = 50;
        this.containers = [];
        this._setActivePage(undefined);
        this.resetTemplatePreviewContext();
        this.haspobject = undefined;
        this._offPageRecoveryObject = null;
    }

    clearPageContent(pageId) {
        const page = this.findPage(pageId);
        if (!page) return false;
        // Destroy only direct HaspObject children — avoids false positives from
        // internal draggable Konva nodes inside composite objects (gauge valueText, etc.)
        [...page.getChildren()]
            .filter(child => child instanceof HaspObject)
            .forEach(child => child.destroy());
        page.top_haspid = 1;
        this.containers = this.containers.filter(c => c.pageid !== pageId);
        return true;
    }

    getPagenumbers() {
        var commands = [''];
        this.getChildren().forEach(page => {
            if (page instanceof HaspPage) {
                commands.push(page.haspid);
            }
        });
        return commands;
    }

    getObjectCommands(objectEvent, node) {
        const variables = new Set(getEventVariables(node, objectEvent));
        return ['', ...getAvailableCommands()
            .filter(definition => (definition.requiresVariables ?? []).every(variable => variables.has(variable)))
            .map(definition => serializeCommandAction({
                commandId: definition.id,
                parameters: Object.fromEntries((definition.parameters ?? [])
                    .map(parameter => [parameter.id, parameter.default ?? ''])),
            }))
            .filter(Boolean)].sort();
    }

    findPage(id) {
        var fpage = undefined;
        this.getChildren().forEach(page => {
            if (page instanceof HaspPage) {
                if (page.haspid == id) {
                    fpage = page;
                    return;
                }
            }
        });
        return fpage;
    }

    copySelectedObject(uuidMap) { return this.clipboard.copySelectedObject(uuidMap); }

    _nextRegularPageId() {
        const existing = this.children
            .filter(c => c instanceof HaspPage && !(c instanceof HaspTemplatePage) && c.haspid > 0)
            .map(c => c.haspid);
        return existing.length === 0 ? 1 : Math.max(...existing) + 1;
    }

    /**
     * Reorder regular pages and reassign sequential haspids (1, 2, 3...).
     * Page 0 is never moved. Template pages keep their IDs but their
     * targetPage/underlayPage references are remapped to the new numbering.
     * @param {HaspPage[]} newOrder - regular pages (excluding page 0) in desired order
     */
    reorderPages(newOrder) {
        if (!newOrder || newOrder.length === 0) return;

        // Build old→new haspid mapping
        const mapping = new Map();
        newOrder.forEach((page, index) => {
            const newId = index + 1;
            if (page.haspid !== newId) mapping.set(page.haspid, newId);
        });
        if (mapping.size === 0) return;

        // Apply new haspids to regular pages
        newOrder.forEach((page, index) => {
            const newId = index + 1;
            if (page.comment === 'Page ' + page.haspid) page.comment = 'Page ' + newId;
            page.haspid = newId;
        });

        // Advance counter past the last assigned ID
        this.pageId = newOrder.length + 1;

        // Remap page references across all pages
        this.children.filter(c => c instanceof HaspPage).forEach(page => {
            if (page.page_limit_commands) {
                page.page_limit_commands.forEach(cmd => {
                    if (mapping.has(cmd.page_limit_number))
                        cmd.page_limit_number = mapping.get(cmd.page_limit_number);
                });
            }
            if (page instanceof HaspTemplatePage) {
                if (page.targetPage !== null && mapping.has(page.targetPage)) {
                    const newTarget = mapping.get(page.targetPage);
                    if (page.comment === 'Template → P' + page.targetPage)
                        page.comment = 'Template → P' + newTarget;
                    page.targetPage = newTarget;
                }
                if (page.underlayPage != null && mapping.has(page.underlayPage))
                    page.underlayPage = mapping.get(page.underlayPage);
            }
        });

        this.batchDraw();
    }

    addPage(config) {
        const isTemplate = config.isTemplate || config.obj === 'template_page';
        if (isTemplate) {
            // Template pages use IDs 50+ — separate counter keeps them apart
            if (config.page !== undefined && config.page >= 50)
                this.templatePageId = config.page;
            config.haspid = this.templatePageId++;
            config.obj = 'template_page';
        } else {
            if (config.page !== undefined && config.page < 50) {
                config.haspid = config.page;
            } else {
                config.haspid = this._nextRegularPageId();
            }
            this.pageId = config.haspid + 1;
            config.obj = config.obj ?? 'page';
        }
        config.theme = this.theme;
        config.width = this.pageWidth;
        config.height = this.pageHeight;
        config.displayShape ??= this.pageShape ?? 'rectangular';
        config.show_id = this.showId;
        var newPage = new objectTypelookup[config.obj](config);

        if (this.activePage !== undefined) {
            this.activePage.setActive(false);
            
            // Position new page based on mode
            if (this.singlePageMode) {
                // In single page mode, all pages are at (0, 0)
                newPage.x(0);
                newPage.y(0);
            } else {
                // Original grid layout (4 pages per row)
                newPage.x((this.activePage.x() + this.activePage.width() + this.gridSize))

                if (!(newPage.haspid % 4)) {
                    newPage.y(this.activePage.y() + this.activePage.height() + this.gridSize)
                    newPage.x(0);
                } else {
                    newPage.y(this.activePage.y())
                }
            }
        } else {
            // First page always at (0, 0)
            newPage.x(0);
            newPage.y(0);
        }
        
        this.add(newPage);
        if (newPage.haspid === 0) this.pageZero = newPage;

        this.haspobject = newPage;
        this._setActivePage(newPage);
        this.activePage.setActive(true);
        this.activePage.moveToTop();

        // Update page visibility in single page mode
        if (this.singlePageMode) {
            this.updatePageVisibility();
        }

        // A freshly added non-zero page just needs the *existing* page-0
        // bitmap attached — page 0 itself hasn't changed, so reuse the last
        // capture instead of re-rasterizing it. This matters a lot when
        // importing a project with many pages: without it, every single
        // addPage() call would redo the full page-0 capture-and-broadcast.
        if (this.redrawOverlay && newPage.haspid !== 0) {
            if (this._cachedOverlayImage) {
                newPage.setPageZeroOverlay(this._cachedOverlayImage);
            } else {
                this.updatePageZeroOverlay('addPage');
            }
        }
        this.updateOffPageMask();
        return newPage;
    }

    addObject(config) {
        // console.log(config)
        if (this.haspobject !== undefined || isTemplatePage(this.activePage)) {
            config.layer = this;
            const templatePage = isTemplatePage(this.activePage) ? this.activePage : null;
            if (templatePage) {
                // On templates the current selection is the only legal parent;
                // walking upwards would turn a selected leaf into a new root.
                const result = canAddObjectToParent(this.haspobject, config.obj, { templatePage });
                if (!result.valid) {
                    this.layerCallback('add-object-error', { message: result.reason, object: this.haspobject });
                    return null;
                }
                if (!this.haspobject) this.haspobject = result.parent;
            }
            //first we make sure we add to a container and the container should not be a tab
            while (!templatePage && this.haspobject && (!this.haspobject.isContainer() || this.haspobject.obj === 'tab')) {
                this.haspobject = this.haspobject.getParent();
            }
            if (!this.haspobject)
                return

            // Line uses drawing mode: capture points interactively before creating
            if (config.obj === 'line') {
                this.enterLineDrawing(this.haspobject);
                return null;
            }

            if (config.obj === 'connection') {
                this.haspobject = this.activePage;
            }

            //here we should ask the page for a new id
            config.haspid = this.activePage.getNextId();
            config.theme = this.theme;
            config.show_id = this.showId;
            if (config.obj === 'msgbox') {
                config._pageWidth  = this.activePage.width();
                config._pageHeight = this.activePage.height();
            }
            if ((config.obj === 'page' || config.obj === 'template_page') && config.displayShape === undefined) {
                config.displayShape = this.pageShape ?? 'rectangular';
            }
            let newObject;
            newObject = new objectTypelookup[config.obj](config)
            // console.log(newObject);
            if (newObject) {
                const item = this.haspobject.addItem(newObject);
                // if (newObject.isContainer() && !newObject.isType('tab'))
                if (item instanceof HaspObject) {
                    this.haspobject = newObject;
                    this.haspobject.moveToTop();
                    if (newObject.isType('connection')) {
                        placeConnectionBehindPageObjects(this.activePage, newObject);
                    }
                    
                    // Ensure page0 overlay stays on top when adding objects to other pages
                    const objectPage = getObjectPage(this.haspobject);
                    if (this.redrawOverlay && objectPage && objectPage.pageZeroOverlay) {
                        objectPage.pageZeroOverlay.moveToTop();
                    }
                }
            }
            // Some container types (msgbox/tabview/buttonmatrix) override
            // addItem() without refreshing the overlay themselves, so this
            // stays a belt-and-suspenders check — gated so it's a no-op for
            // objects added anywhere but page 0.
            if (getObjectPage(this.haspobject)?.haspid === 0) {
                this.updatePageZeroOverlay('addObject');
            }
            this.layerCallback('add-object', this.haspobject);
            this.updateOffPageMask();
            return newObject;
        }
        return undefined;
    }

    refreshBadgeConnections() {
        const route = refreshBadgeConnections(this, this.provisionalConnection);
        this.refreshBadgeRings();
        return route;
    }

    refreshBadgeConnectionsForParent(parent) {
        const badges = parent?.find?.(node => node.obj === 'badge') ?? [];
        const badgeIds = new Set(badges.map(badge => String(badge.id())));
        if (badgeIds.size < 2) return null;

        const route = refreshBadgeConnections(
            this,
            this.provisionalConnection,
            connection => badgeIds.has(String(connection.from)) && badgeIds.has(String(connection.to)),
        );
        this.refreshBadgeRings();
        return route;
    }

    refreshBadgeRings() {
        this.find(node => node.obj === 'badge').forEach(badge => badge.layoutBadge());
        this.batchDraw();
    }

    refreshConnectionsForBadgeValue(badge) {
        const badgeId = badge?.id?.();
        this.find(node => node.obj === 'connection')
            .filter(connection =>
                (connection.value_source === 'from' && connection.from === badgeId)
                || (connection.value_source === 'to' && connection.to === badgeId),
            )
            .forEach(connection => connection.updateFlowState());
        this.refreshBadgeRings();
    }

    _badgeAtPagePoint(point, excludedId) {
        const badges = this.find(node => node.obj === 'badge');
        return badges.find(badge => {
            if (badge.id() === excludedId || getObjectPage(badge) !== this.activePage) return false;
            const geometry = badge.getConnectionGeometry();
            const dx = point.x - geometry.centerX;
            const dy = point.y - geometry.centerY;
            return dx * dx + dy * dy <= geometry.radius * geometry.radius;
        });
    }

    startBadgeConnectionDrag(sourceBadge, side, handle) {
        const id = uuidv4();
        const creationDefaults = normalizeConnectionCreationDefaults(this.connectionDefaults);
        this.provisionalConnection = {
            id, from: sourceBadge.id(), to: '', from_side: side, to_side: 'auto', sourceBadge,
            creationDefaults,
            line_color: resolvedSourceConnectionColor(sourceBadge),
        };
        this._connectionPreview = new ConnectionRouteShape({
            routePoints: [], cornerRadius: creationDefaults.corner_radius,
            stroke: '#1677a8', strokeWidth: creationDefaults.line_width,
            lineCap: 'round', lineJoin: 'round', listening: false,
            obj: 'connection_preview',
        });
        this._connectionTargetHighlight = new Konva.Circle({
            stroke: '#36a9cf', strokeWidth: 3, dash: [6, 4], listening: false, visible: false,
            obj: 'connection_target_highlight',
        });
        this.activePage.add(this._connectionPreview, this._connectionTargetHighlight);
        sourceBadge.moveToTop();
        sourceBadge.connectionHandles?.moveToTop();
        this.updateBadgeConnectionDrag();
    }

    updateBadgeConnectionDrag() {
        if (!this.provisionalConnection) return;
        const point = this.activePage.getRelativePointerPosition();
        if (!point) return;
        const target = this._badgeAtPagePoint(point, this.provisionalConnection.from);
        this.provisionalConnection.to = target?.id() ?? '';
        this.provisionalConnection.to_side = 'auto';
        const route = this.refreshBadgeConnections();
        if (route?.points) {
            this._connectionPreview.setRoute(route.points, this.provisionalConnection.creationDefaults.corner_radius);
        } else {
            const source = route?.from?.point ?? point;
            const sourceSide = route?.from?.side ?? this.provisionalConnection.from_side;
            const targetSide = { left: 'right', right: 'left', top: 'bottom', bottom: 'top' }[sourceSide];
            const points = routeOrthogonalConnection(source, sourceSide, point, targetSide);
            this._connectionPreview.setRoute(points, this.provisionalConnection.creationDefaults.corner_radius);
        }
        if (target) {
            const geometry = target.getConnectionGeometry();
            this._connectionTargetHighlight.setAttrs({
                x: geometry.centerX, y: geometry.centerY, radius: geometry.radius + 5, visible: true,
            });
        } else {
            this._connectionTargetHighlight.visible(false);
        }
        this.batchDraw();
    }

    finishBadgeConnectionDrag() {
        if (!this.provisionalConnection) return null;
        const provisional = this.provisionalConnection;
        const model = createConnectionConfig(
            provisional.id, provisional.from, provisional.to, provisional.from_side,
            provisional.line_color, provisional.creationDefaults,
        );
        this._connectionPreview?.destroy();
        this._connectionTargetHighlight?.destroy();
        this.provisionalConnection = null;
        provisional.sourceBadge.layoutConnectionHandles();
        if (!model) {
            this.refreshBadgeConnections();
            return null;
        }
        const connection = this.addObject({ obj: 'connection', _uuid: model.id, ...model });
        this.refreshBadgeConnections();
        if (connection) this.setSelectedNode(selectionAfterConnectionDrop(provisional.sourceBadge));
        return connection;
    }

    /**
     * we need only one overlay for it is the same on every page
     * But a konva object may only be added to one parent
     * 
     * when something changes on page0 we should call this.
     */
    updatePageZeroOverlay(event) {
        if (this.redrawOverlay) {
            // A capture is already in flight (its Image.onload is async) —
            // coalesce into a single follow-up run instead of starting an
            // overlapping capture, which would race on pageZero's shared
            // visibility/opacity toggles and stack up draw() calls.
            if (this._overlayCapturing) {
                this._overlayCapturePending = true;
                return;
            }
            //check the manipulated object is on page zero
            //OR if we added a new page
            // var objectPage = getObjectPage(this.haspobject)
            //when the object was deleted
            // if (objectPage === undefined) objectPage = this.pageZero;
            if (this.pageZero !== undefined) {
                // if ((objectPage.obj === 'page' && objectPage.haspid === 0) || this.haspobject.obj === 'page') {
                // Create a new PNG image of page0, this image is then added to every page as a forground image
                this._overlayCapturing = true;
                var updatedPageZeroImage = new Image();

                // Store original visibility state of page0
                const wasPage0Visible = this.pageZero.visible();

                //after the src is set the onload is executed;
                updatedPageZeroImage.onload = () => {
                    //this gives every page except page 0 a page overlay image
                    this.children.forEach(child => {
                        if (child.obj === 'page' && child.haspid !== 0) { // no overlay over page0
                            child.setPageZeroOverlay(updatedPageZeroImage);
                        }
                    });

                    // Restore page0 visibility state
                    this.pageZero.visible(wasPage0Visible);

                    // Cache so newly added pages (see addPage()) can reuse
                    // this bitmap without forcing a fresh capture.
                    this._cachedOverlayImage = updatedPageZeroImage;

                    this.draw();  // Redraw the layer to reflect changes
                    this._overlayCapturing = false;
                    if (this._overlayCapturePending) {
                        this._overlayCapturePending = false;
                        this.updatePageZeroOverlay(event);
                    }
                    this.layerCallback('overlay-updated', null);
                };
                
                // Temporarily make page0 visible to capture its content
                this.pageZero.visible(true);
                
                //set background transparent and idobj to invisible
                this.pageZero.rect.opacity(0)
                const idvis = this.pageZero.idTextObj.visible();
                this.pageZero.idTextObj.visible(false);
                
                // Draw the layer to ensure all changes are rendered before capturing
                this.draw();
                
                // Capture the page in its own coordinate space. Konva's node
                // export includes ancestor transforms, so crop at the page's
                // absolute position and cancel the stage zoom. Without this,
                // the current viewport offset/zoom is baked into the bitmap;
                // placing that bitmap at (0, 0) on other pages shifts page 0.
                updatedPageZeroImage.src = this.pageZero.toDataURL(
                    pageOverlayCaptureOptions(this.pageZero),
                );
                
                // Restore visibility states
                this.pageZero.idTextObj.visible(idvis);
                this.pageZero.rect.opacity(1)
            }
            // }
        } else {
            this._cachedOverlayImage = undefined;
            this.children.forEach(child => {
                if (child.obj === 'page' && child.haspid !== 0) // no overlay over page0
                    child.setPageZeroOverlay(undefined);
            });
            this.draw();  // Redraw the layer to reflect changes
        }
    }

    /**
     * Replace one live widget with another renderer while keeping its Designer
     * UUID, OpenHASP ID, parent, child order and page. The target is created
     * through the normal import path, so defaults, themes and schema wiring
     * are exactly the same as for a newly loaded target widget.
     */
    replaceObjectWithConfig(objectUuid, config) {
        const current = this.findOne('#' + objectUuid);
        if (!(current instanceof HaspObject)) return null;

        const parent = current.getParent();
        const childObjects = (current.getChildren?.() ?? []).filter(child => child instanceof HaspObject);
        const oldIndex = current.zIndex();
        const wasSelected = this.selectedObjects.includes(current) || this.haspobject === current;
        const importConfig = { ...config };
        delete importConfig._uuid;
        importConfig.id = current.haspid;
        importConfig.page ??= getObjectPage(current)?.haspid;
        if (parent && parent.obj !== 'page' && parent.obj !== 'template_page') {
            importConfig.parentid = parent.haspid;
        }

        if (wasSelected && typeof current.setSelected === 'function') current.setSelected(false);
        const replacement = this.importObject(importConfig);
        if (!(replacement instanceof HaspObject)) {
            if (wasSelected) this.setSelectedNode(current);
            return null;
        }

        // A conversion may not silently discard a nested object. Future
        // container conversions can retain children by moving them first.
        if (childObjects.length > 0 && !replacement.isContainer()) {
            replacement.destroy();
            if (wasSelected) this.setSelectedNode(current);
            throw new Error(`Cannot convert ${current.obj}: the target cannot contain its children.`);
        }

        childObjects.forEach(child => replacement.add(child));
        this.containers = this.containers.filter(entry => entry.container !== current);
        current.destroy();

        replacement.id(objectUuid);
        replacement.zIndex(Math.min(oldIndex, Math.max(0, parent?.getChildren?.().length - 1 ?? 0)));

        if (wasSelected) this.setSelectedNode(replacement);
        else if (this.haspobject === current) this.haspobject = replacement;
        this.refreshBadgeConnections();
        this.updateOffPageMask();
        this.batchDraw();
        this.layerCallback('object-converted', replacement);
        return replacement;
    }

    importObject(config, { enforceTemplateRules = false } = {}) {
        normalizeLegacyConnectionConfig(config);
        config.layer = this;
        config.haspid = config.id;
        config.width = config.w;
        config.height = config.h;
        // qrcode exports as 'size' (square), not 'w'/'h'
        if (config.obj === 'qrcode' && config.size !== undefined) {
            config.width = config.size;
            config.height = config.size;
        }
        config.show_id = this.showId;
        // config.type = config.obj;
        config.theme = this.theme;
        
        // Auto-detect OpenHASP icons in Private Use Area (U+E000-U+F8FF) and set font family to Material Icons
        if (config.text && /[\uE000-\uF8FF]/.test(config.text)) {
            if (!config.text_font_family) {
                config.text_font_family = 'Material Icons';
            }
        }
        
        let newObject = undefined;
        config.id = undefined //otherwise the Konva object get messed up
        if (config.page === undefined) {
            config.page = this.activePage.haspid;
        }
        if ((config.obj === 'page' || config.obj === 'template_page') && config.displayShape === undefined) {
            config.displayShape = this.pageShape ?? 'rectangular';
        }

        //find parent
        if (config.parentid) {
            //find the container by the parent_id
            const page = this.findPage(config.page);
            const parent = page?.find?.(node => node instanceof HaspObject &&
                Number(node.haspid) === Number(config.parentid))[0];
            if (parent) this.haspobject = parent;
            else {
                const legacyParent = this.containers.find(container =>
                    container.haspid == config.parentid && container.pageid == config.page);
                if (legacyParent) this.haspobject = legacyParent.container;
            }
        } else if (config.page !== undefined) {
            //find the page as the parent
            const page = this.findPage(config.page);
            if (page)
                this.haspobject = page;
            else {
                this.addPage({ "page": config.page })
            }
        } else {
            //if no parent container or specific page was found then use the activepage
            this.haspobject = this.activePage;
        }
        if (enforceTemplateRules && isTemplatePage(getObjectPage(this.haspobject))) {
            const result = canAddObjectToParent(this.haspobject, config.obj);
            if (!result.valid) {
                this.layerCallback('add-object-error', { message: result.reason, object: this.haspobject });
                return undefined;
            }
        }
        //now we know where to add the new object
        //create the new object
        if (config.obj in objectTypelookup) {
            newObject = new objectTypelookup[config.obj](config)
        }

        if (newObject) {
            if (newObject.isContainer()) {
                //when we have a container object save it to the containers store
                const pageid = config.page;
                const haspid = config.haspid;
                const container = newObject;
                //containers are needed to find the object when we have a parent_id
                this.containers.push({ pageid, haspid, container });
            }
            //now add the new object
            this.haspobject.addItem(newObject);
            this.activePage.setNextId(newObject.haspid + 1);
            this.validateRuntimeObjectIds();
            this.refreshBadgeConnections();
        }
        return newObject;
    }

    validateRuntimeObjectIds() {
        this.runtimeIdValidationErrors = validateDesignerObjectIds(this);
        for (const { object } of collectDesignerObjects(this)) {
            const result = validateDesignerObjectId(object, object.haspid, this);
            object._runtimeIdValidationError = result.valid ? '' : result.error;
        }
        return this.runtimeIdValidationErrors;
    }

    setObjectHaspId(object, value) {
        if (!object) return false;
        const numericId = Number(value);
        const result = validateDesignerObjectId(object, numericId, this);
        if (!result.valid) {
            object._runtimeIdValidationError = result.error;
            this.runtimeIdValidationErrors = [result.error];
            return false;
        }
        object._runtimeIdValidationError = '';
        object.haspid = numericId;
        object.updateId?.(numericId);
        this.validateRuntimeObjectIds();
        object.adjust('node-haspid', object);
        return true;
    }

    getSuggestedObjectHaspId(object) {
        if (!object) return null;
        let owner = object;
        while (owner?.getParent?.() && owner.obj !== 'page' && owner.obj !== 'template_page') {
            owner = owner.getParent();
        }
        try {
            return allocateDesignerObjectId(owner, this, 1);
        } catch {
            return null;
        }
    }

    updatePageDisplayShape(shape) {
        this.pageShape = shape === 'round' ? 'round' : 'rectangular';
        this.children.forEach((child) => {
            if (child instanceof HaspPage && typeof child.setDisplayShape === 'function') {
                child.setNewSize({ width: this.pageWidth, height: this.pageHeight });
                child.setDisplayShape(this.pageShape);
            }
        });
        this.updateOffPageMask();
        this.batchDraw();
    }

    /**
     * Serialize the currently selected object (and its children) into a
     * relative-coordinate template suitable for storing as a custom component.
     * Coordinates are normalized so the top-left of the bounding box is (0, 0).
     */
    exportSelectionAsTemplate() { return exportSelectionAsTemplate(this); }

    exportSelectionThumbnail(targetSize = 128) { return exportSelectionThumbnail(this, targetSize); }

    /**
     * Place all objects from a custom component template onto the active page.
     * Each object receives a fresh haspid; positions are centered on the page.
     * Only root-level objects (no parentid within the template) get the centering
     * offset — child objects keep their parent-relative coordinates as-is.
     */
    placeCustomComponent(component) {
        if (!this.activePage || !component?.objects?.length) return;

        // Resolve target container: use current selection if it can hold children,
        // otherwise fall back to the active page.
        const selection     = this.haspobject;
        const targetIsPage  = !selection || selection.isType('page') || selection.isType('template_page');
        const target        = (!targetIsPage && selection.isContainer())
            ? selection
            : this.activePage;
        const placeOnPage   = target.isType('page') || target.isType('template_page');

        // Container dimensions for centering
        const containerW = placeOnPage ? this.pageWidth  : target.width();
        const containerH = placeOnPage ? this.pageHeight : target.height();

        const objs = component.objects;
        const templateIds = new Set(objs.map(o => o.id));
        const rootObjects = objs.filter(o => o.parentid == null || !templateIds.has(o.parentid));
        if (isTemplatePage(this.activePage)) {
            const result = canAddObjectsToParent(target, rootObjects.map(o => o.obj));
            if (!result.valid) {
                this.layerCallback('add-object-error', { message: result.reason, object: target });
                return;
            }
        }

        // Bounding box from root-level objects only
        let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
        objs.forEach(o => {
            if (o.parentid != null && templateIds.has(o.parentid)) return;
            const x = o.x ?? 0, y = o.y ?? 0, w = o.w ?? 0, h = o.h ?? 0;
            if (x < minX) minX = x;
            if (y < minY) minY = y;
            if (x + w > maxX) maxX = x + w;
            if (y + h > maxY) maxY = y + h;
        });
        if (!isFinite(minX)) { minX = 0; minY = 0; maxX = 0; maxY = 0; }

        const compW   = maxX - minX;
        const compH   = maxY - minY;
        const offsetX = Math.round((containerW - compW) / 2) - minX;
        const offsetY = Math.round((containerH - compH) / 2) - minY;

        // Build old-id → new-id map so parent references stay consistent
        const idMap = new Map();
        const reservedIds = new Set();
        objs.forEach(o => {
            idMap.set(o.id, this.activePage.getNextId({ reservedIds }));
        });
        const endpointIdMap = new Map();

        this.haspobject = target;

        objs.forEach(o => {
            const isChild = o.parentid != null && templateIds.has(o.parentid);
            const config = {
                ...o,
                id:       idMap.get(o.id),
                ...(o.obj === 'connection' ? {
                    from: endpointIdMap.get(String(o.from)) ?? o.from,
                    to:   endpointIdMap.get(String(o.to)) ?? o.to,
                } : {}),
                x:        isChild ? (o.x ?? 0) : (o.x ?? 0) + offsetX,
                y:        isChild ? (o.y ?? 0) : (o.y ?? 0) + offsetY,
                page:     this.activePage.haspid,
                // Root objects become children of the target container when it is
                // not a page; children keep their remapped parent id as before.
                parentid: isChild
                    ? idMap.get(o.parentid)
                    : (placeOnPage ? undefined : target.haspid),
                custom_component_id:   component.id,
                custom_component_name: component.name,
            };
            const newObject = this.importObject(config);
            if (newObject && o.obj !== 'connection') {
                endpointIdMap.set(String(o.id), newObject.id());
            }
        });

        this.haspobject = target;
        placeComponentLogic(this.activePage, component.logic, id => endpointIdMap.get(String(id)),
            { x: offsetX, y: offsetY }).forEach(message => console.warn(message));
        placeComponentHass(this.activePage, component.hass, id => endpointIdMap.get(String(id)),
            { x: offsetX, y: offsetY }).forEach(message => console.warn(message));
        // Components saved before event/action nodes carry their actions per object.
        migratePageActions(this.activePage, { plateWidth: this.pageWidth });
        // Components saved before Home Assistant flows carry their hass_config per object.
        migratePageHass(this.activePage, { plateWidth: this.pageWidth });
        if (getObjectPage(target)?.haspid === 0) {
            this.updatePageZeroOverlay('placeCustomComponent');
        }
        this.layerCallback('add-object', this.haspobject);
    }

    setObjectColor(color) {
        if (this.haspobject !== undefined) {
            this.haspobject.setColor(color);
        }
    }

    /**
     * Switch to a specific page
     * @param {HaspPage} page - The page to switch to
     * @param {object} [options]
     * @param {HaspObject|null} [options.sourceObject] source of a transient
     *        template preview; only relevant for template pages
     */
    switchToPage(page, options = {}) {
        if (!page || !(page instanceof HaspPage)) return;

        if (page instanceof HaspTemplatePage) {
            this.setTemplatePreviewContext(page, options.sourceObject ?? null);
        } else {
            this.resetTemplatePreviewContext();
        }

        if (this.activePage !== page &&
            (this.activePage instanceof HaspTemplatePage || page instanceof HaspTemplatePage)) {
            this._deselectCurrent();
        }
        
        // Deactivate current page if exists
        if (this.activePage && this.activePage !== page) {
            this.activePage.setActive(false);
        }
        
        // Set new active page
        this._setActivePage(page);
        this.haspobject = page;
        this.activePage.setActive(true);
        this.activePage.moveToTop();
        
        // Ensure overlay stays on top when switching pages
        if (this.redrawOverlay && this.activePage.pageZeroOverlay) {
            this.activePage.pageZeroOverlay.moveToTop();
        }
        
        // Also restore or apply the read-only template preview context in
        // all-pages mode, where visibility itself does not otherwise change.
        this.updatePageVisibility();
        
        // Clear transformer selection
        this.transformer.nodes([]);
        
        this.draw();
        this.layerCallback('page-switch', this.activePage);
    }

    /**
     * Open a template as a transient Designer preview for a source object.
     * The template's persisted targetPage remains the runtime/export target;
     * sourceObject only chooses the regular page rendered behind it.
     */
    previewTemplate(templatePage, sourceObject = null) {
        if (!(templatePage instanceof HaspTemplatePage)) return false;
        this.switchToPage(templatePage, { sourceObject });
        return true;
    }

    setTemplatePreviewContext(activeTemplate, sourceObject = null) {
        const pages = this.children.filter(child => child instanceof HaspPage);
        this.templatePreviewState = createTemplatePreviewState({
            pages,
            activeTemplate,
            sourceObject: sourceObject ?? null,
        });
    }

    resetTemplatePreviewContext() {
        this.templatePreviewState = resetTemplatePreviewState();
        this._notifyTemplatePreviewContext();
    }

    /**
     * Notify Designer panels that the transient template instance changed.
     * The context remains on the layer; this event is only a render/update
     * signal and cannot become part of an object or template export.
     */
    _notifyTemplatePreviewContext() {
        this.fire('template-preview-context', { state: this.templatePreviewState });
    }

    /**
     * Update visibility of all pages based on singlePageMode
     */
    updatePageVisibility() {
        const pages = this.children.filter(child => child instanceof HaspPage);
        const activeTemplate = this.activePage instanceof HaspTemplatePage ? this.activePage : null;
        const sourceObject = activeTemplate && this.templatePreviewState?.activeTemplate === activeTemplate
            ? this.templatePreviewState.sourceObject
            : null;
        this.templatePreviewState = createTemplatePreviewState({
            pages,
            activeTemplate,
            sourceObject,
        });
        const { visiblePages, contextPage } = getPagePreviewVisibility(pages, this.activePage, {
            singlePageMode: this.singlePageMode,
            previewState: this.templatePreviewState,
        });

        if (!this.singlePageMode) {
            // Show all pages
            pages.forEach(page => page.visible(true));
        } else {
            // In template edit mode, previewPage is an additional live canvas
            // context. Page zero otherwise keeps its existing
            // single-page visibility behavior.
            pages.forEach(page => page.visible(visiblePages.has(page)));
        }

        this._setTemplateContextPage(contextPage);
        if (this.activePage instanceof HaspTemplatePage) this.activePage.moveToTop();
        this._notifyTemplatePreviewContext();
    }

    _setTemplateContextPage(page) {
        setTemplateContextPage(this, page);
    }

    /**
     * Refresh the transient previewPage when template context changes.
     */
    updateTemplateUnderlay() {
        this.updatePageVisibility();
        this.draw();
    }

    /**
     * Toggle between single page mode and all pages mode
     */
    toggleSinglePageMode() {
        this.singlePageMode = !this.singlePageMode;
        this.updatePageVisibility();
        this.repositionPages();
        this.draw();
    }

    /**
     * Reposition pages based on current mode
     */
    repositionPages() {
        if (this.singlePageMode) {
            // Position all pages at the same location (0, 0)
            this.children.forEach(child => {
                if (child instanceof HaspPage) {
                    child.position({ x: 0, y: 0 });
                }
            });
        } else {
            // Position pages in a grid layout (4 per row)
            let pageIndex = 0;
            this.children.forEach(child => {
                if (child instanceof HaspPage) {
                    const row = Math.floor(pageIndex / 4);
                    const col = pageIndex % 4;
                    child.position({
                        x: col * (this.pageWidth + this.gridSize),
                        y: row * (this.pageHeight + this.gridSize)
                    });
                    pageIndex++;
                }
            });
        }
    }

    // ─── Multi-select helpers ────────────────────────────────────────────────

    getSelectedObjects() {
        return this.selectedObjects;
    }

    _startRubberBand(e) {
        const stage = this.getStage();
        if (!stage) return;
        const pos = stage.getPointerPosition();
        if (!pos) return;
        const scale = stage.scaleX();
        const sp = stage.position();
        this._rubberBandOrigin = {
            x: (pos.x - sp.x) / scale,
            y: (pos.y - sp.y) / scale,
        };
        this._rubberBandActive = true;
        this._rubberBandRect.setAttrs({ x: this._rubberBandOrigin.x, y: this._rubberBandOrigin.y, width: 0, height: 0, visible: true });
        this.batchDraw();
    }

    _updateRubberBand() {
        if (!this._rubberBandActive) return;
        const stage = this.getStage();
        const pos = stage.getPointerPosition();
        if (!pos) return;
        const scale = stage.scaleX();
        const sp = stage.position();
        const cx = (pos.x - sp.x) / scale;
        const cy = (pos.y - sp.y) / scale;
        const x = Math.min(this._rubberBandOrigin.x, cx);
        const y = Math.min(this._rubberBandOrigin.y, cy);
        const width = Math.abs(cx - this._rubberBandOrigin.x);
        const height = Math.abs(cy - this._rubberBandOrigin.y);
        this._rubberBandRect.setAttrs({ x, y, width, height });
        this.batchDraw();
    }

    _finishRubberBand() {
        if (!this._rubberBandActive) return;
        this._rubberBandActive = false;
        const rect = {
            x: this._rubberBandRect.x(),
            y: this._rubberBandRect.y(),
            width: this._rubberBandRect.width(),
            height: this._rubberBandRect.height(),
        };
        this._rubberBandRect.visible(false);
        this.batchDraw();
        if (rect.width < 4 && rect.height < 4) return;
        const found = this._getObjectsInRect(rect);
        if (found.length > 0) {
            this.selectedObjects.forEach(o => {
                if (o instanceof HaspObject && typeof o.setSelected === 'function') o.setSelected(false);
            });
            this.selectedObjects = found;
            found.forEach(o => { if (typeof o.setSelected === 'function') o.setSelected(true); });
            this.haspobject = found[0];
            this.updateTransformer();
            this.layerCallback('mouse-down', this.haspobject);
        }
    }

    _getObjectsInRect(rect) {
        const found = [];
        const page = this.activePage;
        if (!page) return found;
        const stage = this.getStage();
        if (!stage) return found;
        const scale = stage.scaleX();
        const sp = stage.position();
        const walk = (node) => {
            if (!node.getChildren) return;
            node.getChildren().forEach(child => {
                if (!(child instanceof HaspObject)) return;
                if (child.isType('page')) return;
                const absPos = child.getAbsolutePosition();
                const cx = (absPos.x - sp.x) / scale;
                const cy = (absPos.y - sp.y) / scale;
                const cw = child.width();
                const ch = child.height();
                if (cx < rect.x + rect.width &&
                    cx + cw > rect.x &&
                    cy < rect.y + rect.height &&
                    cy + ch > rect.y) {
                    found.push(child);
                }
                walk(child);
            });
        };
        walk(page);
        return found;
    }
}

Object.assign(HaspObjectLayer.prototype, LineDrawingMixin);

export { HaspObjectLayer, objectTypelookup }
